import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    HostBinding,
    Inject,
    Input,
    NgZone,
    OnInit,
    Optional,
    Output,
    TemplateRef,
    ViewChild,
    computed,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { OpenedModalsStorageService } from '@frontend/sports/modal/feature';
import { DeviceService, NativeAppService, WINDOW } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import { NgxPopperjsContentComponent, NgxPopperjsPlacements, NgxPopperjsTriggers } from 'ngx-popperjs';
import { Subject, filter, fromEvent, skip, takeUntil } from 'rxjs';

import { UiManagerActions } from '../ui-manager/ui-manager.actions';
import { IUiRootState } from '../ui-manager/ui-manager.state';
import { UiStates } from '../ui-manager/ui-states.enum';
import { ArrowType, DROPDOWN_CUSTOM_CLASS, DropdownOption, OptionType } from './models';

@Component({
    selector: 'ms-dropdown',
    templateUrl: './dropdown.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class DropdownComponent implements OnInit {
    private readonly _window = inject(WINDOW);

    @Input() activeOption?: DropdownOption;
    @Input() autoClose = true;
    @Input() @HostBinding('class.dropdown-disabled') disabled = false;
    @Input() noSelectionText = '';
    @Input() contentClass = '';
    @Input() titleTemplate: TemplateRef<any>;
    @Input() optionTemplate: TemplateRef<any>;
    @Input() textColor?: string;
    @Input() offerBadgeTemplate?: TemplateRef<any>;
    @Input() isIconTemplate = false;
    @Output() optionChange = new EventEmitter<DropdownOption>();
    @Output() dropdownState = new EventEmitter<boolean>();
    @ViewChild('dropDown') dropDown: NgxPopperjsContentComponent;
    @Input() popperClass = 'dropdown-popper';

    @Input() set options(options: DropdownOption[]) {
        this._options.set(options);
    }

    @Input() set viewport(viewport: string | undefined) {
        this._viewport.set(viewport ?? 'body');
    }

    @Input() set dropdownPlacement(placement: 'right' | 'left') {
        this.placement.set(this.placementMap[placement]);
    }

    @HostBinding('class.ms-dropdown') hostCssClass = true;

    @HostBinding('class.dropdown-expanded') get expandedClass(): boolean {
        return this.expanded();
    }

    expanded = signal(false);
    arrowClass = computed(() => (this.expanded() ? 'theme-up' : 'theme-down'));

    optionType = OptionType;
    popperFlip = true;

    private expandedState$ = new Subject<boolean>();

    private get element(): HTMLElement {
        return this.elementRef.nativeElement;
    }

    get isOnEdpModal(): boolean {
        return !!this.openedModals.edpModal;
    }

    _viewport = signal('body');
    _options = signal<DropdownOption[]>([]);
    expandable = computed(() => this._options().length > 1);
    containerClass = computed(() => (this.expandable() ? 'multiple' : 'single'));

    placement = signal(NgxPopperjsPlacements.BOTTOM);
    private readonly placementMap = {
        right: NgxPopperjsPlacements.BOTTOMEND,
        left: NgxPopperjsPlacements.BOTTOMSTART,
    };

    private ngZone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        if (this.customClass) {
            this.popperClass = `dropdown-popper,${this.customClass}`;
        }
    }

    private reset = (): void => {
        if (!this.expanded()) {
            return;
        }
        if (this.expanded() && this.expandable()) {
            this.dropDown?.hide();
        }
        this.expanded.set(false);
        this.expandedState$.next(this.expanded());
        this.trySubscribeToResetTriggers(this.expanded());
    };

    constructor(
        private elementRef: ElementRef,
        private strategy: RxStrategyProvider,
        private deviceService: DeviceService,
        private nativeAppService: NativeAppService,
        public sitecore: Sitecore,
        private store: Store<IUiRootState>,
        private openedModals: OpenedModalsStorageService,
        @Optional() @Inject(DROPDOWN_CUSTOM_CLASS) private readonly customClass?: string,
    ) {
        this.customClass = customClass;

        if (this.nativeAppService.isTerminal) {
            this.popperFlip = false;
        }
    }

    selectOption(option: DropdownOption): void {
        this.reset();
        this.activeOption = option;
        this.scheduleOptionChange(option);
    }

    selectSpecialOption(option: DropdownOption): void {
        this.scheduleOptionChange(option);
    }

    private scheduleOptionChange = (option: DropdownOption) => {
        this.strategy.schedule(() => this.optionChange.emit(option), { scope: this, patchZone: this.ngZone }).subscribe();
    };

    toggleExpandedState(): void {
        this.store.dispatch(UiManagerActions.removeState({ state: UiStates.MarketSubTypeTooltipShown }));

        if (this.disabled || !this.expandable()) {
            return;
        }

        this.expanded.update((value) => !value);
        this.expandedState$.next(this.expanded());
        this.trySubscribeToResetTriggers(this.expanded());
        this.dropdownState.emit(this.expanded());
        if (this.dropDown) {
            if (this.expanded() && this.expandable()) {
                this.dropDown.show();
            } else {
                this.dropDown.hide();
            }
        }
    }

    optionTrackBy(index: number, option: DropdownOption): string | number {
        return option.id;
    }

    getArrowClass(arrowType?: ArrowType): string {
        switch (arrowType) {
            case ArrowType.Left:
                return 'theme-left';
            case ArrowType.Right:
                return 'theme-right';
            default:
                return '';
        }
    }

    private isOwnClick(event: Event): boolean {
        return (
            !!event.target && (this.dropDown.popperViewRef.nativeElement.contains(<Node>event.target) || this.element.contains(<Node>event.target))
        );
    }

    // Reset triggers are: device orientation change and window click events outside dropdown
    private trySubscribeToResetTriggers(expanded: boolean): void {
        if (!this.autoClose || !expanded) {
            return;
        }

        const takeUntil$ = this.expandedState$.pipe(filter((x) => !x));

        this.deviceService.orientation.pipe(skip(1), takeUntilDestroyed(this.destroyRef), takeUntil(takeUntil$)).subscribe(() => this.reset());
        fromEvent(this._window, 'mousedown')
            .pipe(
                filter((e) => !this.isOwnClick(e)),
                takeUntilDestroyed(this.destroyRef),
                takeUntil(takeUntil$),
            )
            .subscribe(() => {
                this.reset();
            });
    }

    protected readonly NgxPopperjsTriggers = NgxPopperjsTriggers;
}
